import React from "react";
import { ChevronUp, ChevronDown } from "react-feather";
import type { ControlSizeKey } from "../../../design-system/tokens/controlSizes";
import {
  Wrap,
  Input,
  StepperColumn,
  StepButton,
  Suffix,
} from "./NumberStepper.styles";

export interface NumberStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  invalid?: boolean;
  size?: ControlSizeKey;
  ariaLabel?: string;
  /**
   * Unit word shown inside the control after the number (e.g. "occurrences").
   * Decorative — the accessible name still comes from `ariaLabel`.
   */
  suffix?: string;
  /** Visual-only inactive state used by disabled recurrence rows. */
  muted?: boolean;
  /** Keeps a suffix next to the numeric value while retaining right-aligned controls. */
  compactSuffix?: boolean;
  /** `sentence` sizes the control to sit inside a sentence (InlinePickTrigger's look); default `field`. */
  appearance?: "field" | "sentence";
}

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

export const NumberStepper: React.FC<NumberStepperProps> = ({
  value,
  onChange,
  min = 1,
  max = 999,
  step = 1,
  invalid = false,
  size = "md",
  ariaLabel,
  suffix,
  muted = false,
  compactSuffix = false,
  appearance = "field",
}) => {
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange(min);
      return;
    }
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      onChange(clamp(parsed, min, max));
    }
  };

  const sentence = appearance === "sentence";
  return (
    <Wrap $invalid={invalid} $size={size} $muted={muted} $sentence={sentence}>
      <Input
        $compactSuffix={compactSuffix && Boolean(suffix)}
        $sentence={sentence}
        $valueLength={String(value).length}
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        onChange={handleInput}
      />
      {suffix && <Suffix aria-hidden>{suffix}</Suffix>}
      <StepperColumn $pushEnd={compactSuffix && Boolean(suffix)}>
        <StepButton
          type="button"
          aria-label="Increase"
          disabled={value >= max}
          onClick={() => onChange(clamp(value + step, min, max))}
        >
          <ChevronUp size={14} />
        </StepButton>
        <StepButton
          type="button"
          aria-label="Decrease"
          disabled={value <= min}
          onClick={() => onChange(clamp(value - step, min, max))}
        >
          <ChevronDown size={14} />
        </StepButton>
      </StepperColumn>
    </Wrap>
  );
};

export default NumberStepper;
